import { differenceInYears } from 'date-fns';
import { ChevronsUpDown, UserPlus } from 'lucide-react';
import { type ReactNode, useEffect, useState } from 'react';

import { Button } from '@/components/ui/button';
import { type PatientOut, usePatients } from '../api/patients-api';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';

interface AssignDocumentPopoverProps {
  /** Disable the trigger while a parent mutation is in flight. */
  disabled?: boolean;
  /** Called once a patient is selected. The component closes itself after. */
  onAssign: (patient: PatientOut) => void;
  /** Render-prop trigger so callers can control button styling. */
  trigger?: ReactNode;
}

const SEARCH_LIMIT = 50;
const DEBOUNCE_MS = 200;

/**
 * Patient picker for adjudicating an Inbox document to a chart.
 *
 * Mirrors the patient Command palette in `NewEncounterPage` so the keyboard
 * and visual affordances stay consistent across the app. Per Avi's brief
 * (2026-04-30), this popover deliberately does NOT offer "Create new patient"
 * — that flow stays separate so users don't tangle two modal interactions.
 *
 * Patient lookup uses server-side ``?search=`` so orgs with hundreds of
 * patients can still find anyone. The Command's built-in client-side
 * filter is disabled (``shouldFilter={false}``) to preserve the server
 * ranking and avoid hiding rows the server already matched.
 */
export function AssignDocumentPopover({ trigger, disabled, onAssign }: AssignDocumentPopoverProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Debounce server requests so we don't fire one per keystroke.
  useEffect(() => {
    const handle = setTimeout(() => setDebouncedSearch(search), DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [search]);

  const trimmedSearch = debouncedSearch.trim();
  const { data: patientsData } = usePatients({
    limit: SEARCH_LIMIT,
    ...(trimmedSearch ? { search: trimmedSearch } : {}),
  });
  const activePatients = (patientsData?.data ?? []).filter((p) => !p.deleted_at);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {trigger ?? (
          <Button size="sm" variant="outline" disabled={disabled}>
            <UserPlus className="h-3.5 w-3.5 mr-1.5" />
            Assign
            <ChevronsUpDown className="ml-1.5 h-3 w-3 opacity-60" />
          </Button>
        )}
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[320px] p-0">
        <Command shouldFilter={false}>
          <CommandInput value={search} placeholder="Search patients..." onValueChange={setSearch} />
          <CommandList>
            <CommandEmpty>
              <div className="py-3 px-2 text-center text-sm text-muted-foreground">
                <p>No patients found.</p>
                <p className="text-xs mt-1">Need a new patient? Create one from the Patients page first.</p>
              </div>
            </CommandEmpty>
            <CommandGroup>
              {activePatients.map((patient) => (
                <CommandItem
                  key={patient.id}
                  value={patient.id}
                  onSelect={() => {
                    onAssign(patient);
                    setOpen(false);
                  }}>
                  <div className="flex flex-col">
                    <span className="font-medium">
                      {patient.metadata_?.last_name ?? ''}, {patient.metadata_?.first_name ?? ''}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      MRN: {patient.medical_record_number}
                      {patient.metadata_?.date_of_birth && (
                        <>
                          {' — '}
                          {differenceInYears(new Date(), new Date(patient.metadata_.date_of_birth))}y
                        </>
                      )}
                    </span>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

